import React from 'react';
import Divider from '@/components/atoms/divider/divider';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import { MainNavigationMenuItem } from '@/components/organisms/main-navigation/main-navigation';
import ThemeSearchCard, {
  ThemeSearchCardProps,
} from '@/components/organisms/theme-search-card/theme-search-card';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import MasonryGrid from '@/components/utilities/masonry-grid/masonry-grid';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './main-navigation-large-dropdown-links.module.scss';

export interface MainNavigationLargeDropdownLinksProps {
  /** The card to show in the dropdown */
  card?: (ThemeSearchCardProps & { title: string }) | null;
  /** The text to show as a headline */
  headline: string;
  /** the links to show */
  items?: MainNavigationMenuItem[];
  /** The layout to use for the links */
  layout?: Content.Components['NavigationRootItemDto']['layout'];
  /** Masonry grid settings */
  masonrySettings?: Content.Components['NavigationRootItemDto']['masonrySettings'];
  /** An event that is triggered on click */
  onClick?: () => void;
}

const MainNavigationLargeDropdownLinks = ({
  card,
  headline,
  items = [],
  layout = 'threeColumns',
  masonrySettings,
  onClick,
}: MainNavigationLargeDropdownLinksProps) => {
  const isDesktop = useWindowSize('desktop');
  const useMasonryGrid = layout === 'masonry';
  const desiredColumns =
    masonrySettings?.columnNumber === 'threeColumns' ? 3 : 4;
  const masonryColumns = isDesktop
    ? desiredColumns
    : Math.min(desiredColumns, 3);

  type MainNavigationSubItem = NonNullable<
    MainNavigationMenuItem['items']
  >[number];

  const renderSubItem = (
    subItem: MainNavigationSubItem,
    subItemIndex: number,
  ) => (
    <Link
      key={subItem.link.url || subItemIndex}
      className={styles.link}
      onClick={onClick}
      url={subItem.link.url}
    >
      <Icon className={styles.linkIcon} name="ArrowRight" size={16} />

      <Typography tagStyle="bodyMedium">{subItem.link.text}</Typography>
    </Link>
  );

  const renderItem = (item: MainNavigationMenuItem, itemIndex: number) => (
    <div key={item.link.url || itemIndex} className={styles.section}>
      <Link
        className={styles.sectionHeader}
        onClick={onClick}
        url={item.link.url}
      >
        <Typography
          className={styles.sectionHeaderText}
          tagStyle="bodyLargeBold"
        >
          {item.link.text}
        </Typography>

        <Icon
          className={styles.sectionHeaderIcon}
          name="ArrowRight"
          size={16}
        />
      </Link>

      {item.description && (
        <Typography tagStyle="bodyMedium">{item.description}</Typography>
      )}

      {item.items && item.items.length > 0 && (
        <div className={styles.links}>{item.items.map(renderSubItem)}</div>
      )}
    </div>
  );

  return (
    <Container className={styles.component}>
      <Row>
        <Column className={styles.headlineWrapper}>
          <Typography className={styles.headline} tagStyle="displayMedium">
            {headline}
          </Typography>

          <Divider />
        </Column>
      </Row>

      <Row>
        {card && (
          <Column width={{ laptop: 3 }}>
            <ThemeSearchCard
              {...card}
              aspectRatio="oneByOne"
              headline={card.title}
            />
          </Column>
        )}

        <Column width={{ laptop: card ? 9 : 12 }}>
          {useMasonryGrid ? (
            <MasonryGrid
              balanced={masonrySettings?.useCompactLayout}
              className={styles.row}
              columns={masonryColumns}
              fillDirection={masonrySettings?.fillDirection}
              gap={24}
              itemClassName={styles.column}
            >
              {items.map(renderItem)}
            </MasonryGrid>
          ) : (
            <Row className={styles.row}>
              {items.map((item, itemIndex) => (
                <Column
                  key={item.link.url || itemIndex}
                  className={styles.column}
                  width={{ laptop: layout === 'threeColumns' ? 4 : 3 }}
                >
                  {renderItem(item, itemIndex)}
                </Column>
              ))}
            </Row>
          )}
        </Column>
      </Row>
    </Container>
  );
};

MainNavigationLargeDropdownLinks.displayName =
  'MainNavigationLargeDropdownLinks';

export default MainNavigationLargeDropdownLinks;
